import { gettext } from '@eventbrite/i18n';
import {
    Avatar,
    CheckChunkierIcon,
    PlusIcon,
    Typography,
} from '@eventbrite/marmalade';
import React, { useEffect, useRef } from 'react';
import { useOrganizerFollow } from '../hooks/useOrganizerFollow';
import styles from './OrganizerFollowRow.module.scss';

interface OrganizerFollowRowProps {
    organizerId?: string;
    organizerName?: string;
    organizerImageUrl?: string;
    isAuthenticated?: boolean;
    className?: string;
}

export const OrganizerFollowRow: React.FC<OrganizerFollowRowProps> = ({
    organizerId,
    organizerName,
    organizerImageUrl,
    isAuthenticated,
    className,
}) => {
    const { isFollowing, toggleFollow, trackFollowButtonView } =
        useOrganizerFollow({
            organizerId,
            isAuthenticated,
        });

    const followButtonRef = useRef<HTMLButtonElement>(null);
    const hasTrackedView = useRef(false);

    useEffect(() => {
        const buttonEl = followButtonRef.current;
        if (!buttonEl) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasTrackedView.current) {
                    hasTrackedView.current = true;
                    trackFollowButtonView();
                }
            },
            { threshold: 0.5 },
        );

        observer.observe(buttonEl);
        return () => observer.disconnect();
    }, [trackFollowButtonView]);

    const organizerInitial = organizerName?.charAt(0).toUpperCase();

    return (
        <div className={`${styles.organizerRow} ${className || ''}`}>
            <div className={styles.organizerInfo}>
                <Avatar
                    imageSrc={organizerImageUrl}
                    imageAlt={organizerName}
                    text={organizerInitial}
                    size="small"
                    className={styles.organizerAvatar}
                />
                <Typography variant="body-sm" color="neutral-600">
                    {gettext('By')} <strong>{organizerName}</strong>
                </Typography>
            </div>
            <button
                ref={followButtonRef}
                className={styles.followButton}
                onClick={toggleFollow}
                aria-label={
                    isFollowing
                        ? `${gettext('Unfollow')} ${organizerName}`
                        : `${gettext('Follow')} ${organizerName}`
                }
            >
                {isFollowing ? (
                    <CheckChunkierIcon size="xsmall" />
                ) : (
                    <PlusIcon size="xsmall" />
                )}
                {isFollowing ? gettext('Following') : gettext('Follow')}
            </button>
        </div>
    );
};
